import React, { useState, useEffect } from 'react';
import { X, Search, Shield, CheckCircle, Circle } from 'lucide-react';
import { Button } from '@atoms';
import { trpc } from '@lib/trpc';
import toast from 'react-hot-toast';

interface Role {
  id: string;
  name: string;
  displayName: string;
  description?: string | null;
  permissions?: Permission[];
}

interface Permission {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  resource: string;
  action: string;
  isActive: boolean;
}

interface AdminRolePermissionsModalProps {
  role: Role;
  onClose: () => void;
  onSuccess: () => void;
}

interface GroupedPermissions {
  [resource: string]: Permission[];
}

export const AdminRolePermissionsModal: React.FC<AdminRolePermissionsModalProps> = ({
  role,
  onClose,
  onSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);

  // Fetch all permissions
  const { data: permissionsData } = trpc.admin.getAllPermissions.useQuery();
  
  // Mutations for assigning/removing permissions
  const assignPermissionMutation = trpc.admin.assignRolePermission.useMutation();
  const removePermissionMutation = trpc.admin.removeRolePermission.useMutation();

  // Initialize permissions and selected state
  useEffect(() => {
    if (permissionsData?.data) {
      setAllPermissions(permissionsData.data);
      
      // Initialize selected permissions with current role permissions
      const rolePermissionIds = new Set(
        role.permissions?.map(p => p.id) || []
      );
      setSelectedPermissions(rolePermissionIds);
      setIsLoading(false);
    }
  }, [permissionsData, role.permissions]);

  const filteredPermissions = allPermissions.filter(permission =>
    permission.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (permission.description && permission.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Group permissions by resource
  const groupedPermissions = filteredPermissions.reduce<GroupedPermissions>((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {});

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const toggleAllInResource = (resourcePermissions: Permission[]) => {
    const resourcePermissionIds = resourcePermissions.map(p => p.id);
    const allSelected = resourcePermissionIds.every(id => selectedPermissions.has(id));
    
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Remove all from this resource
        resourcePermissionIds.forEach(id => newSet.delete(id));
      } else {
        // Add all from this resource
        resourcePermissionIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const currentPermissionIds = new Set(role.permissions?.map(p => p.id) || []);
      const toAdd = Array.from(selectedPermissions).filter(id => !currentPermissionIds.has(id));
      const toRemove = Array.from(currentPermissionIds).filter(id => !selectedPermissions.has(id));

      // Add new permissions
      for (const permissionId of toAdd) {
        await assignPermissionMutation.mutateAsync({
          roleId: role.id,
          permissionId,
        });
      }

      // Remove permissions
      for (const permissionId of toRemove) {
        await removePermissionMutation.mutateAsync({
          roleId: role.id,
          permissionId,
        });
      }

      toast.success('Role permissions updated successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to update role permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const formatResourceName = (resource: string) => {
    return resource.charAt(0).toUpperCase() + resource.slice(1).replace(/[-_]/g, ' ');
  };

  const getSelectedCount = () => selectedPermissions.size;
  const getTotalCount = () => allPermissions.length;
  const getChangesCount = () => {
    const currentIds = new Set(role.permissions?.map(p => p.id) || []);
    const toAdd = Array.from(selectedPermissions).filter(id => !currentIds.has(id)).length;
    const toRemove = Array.from(currentIds).filter(id => !selectedPermissions.has(id)).length;
    return toAdd + toRemove;
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-background-primary rounded-lg shadow-xl w-full max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
            <span className="ml-3 text-text-secondary">Loading permissions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-primary rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Manage Permissions
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Role: <span className="font-medium">{role.displayName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Stats */}
        <div className="p-6 border-b border-border-primary flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                placeholder="Search permissions..."
              />
            </div>
            <div className="text-sm text-text-secondary ml-4">
              {getSelectedCount()} of {getTotalCount()} selected
              {getChangesCount() > 0 && (
                <span className="ml-2 text-orange-600">({getChangesCount()} changes)</span>
              )}
            </div>
          </div>
        </div>

        {/* Permissions List */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedPermissions).length === 0 ? (
            <div className="text-center py-8 text-text-secondary">
              {searchTerm ? 'No permissions found matching your search.' : 'No permissions available.'}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedPermissions).map(([resource, permissions]) => {
                const resourcePermissionIds = permissions.map(p => p.id);
                const allSelected = resourcePermissionIds.every(id => selectedPermissions.has(id));
                const someSelected = resourcePermissionIds.some(id => selectedPermissions.has(id));
                const selectedInResource = resourcePermissionIds.filter(id => selectedPermissions.has(id)).length;

                return (
                  <div key={resource} className="bg-background-secondary rounded-lg border border-border-primary">
                    {/* Resource Header */}
                    <div className="p-4 border-b border-border-primary">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleAllInResource(permissions)}
                            className="flex items-center space-x-2 text-text-primary hover:text-primary-600 transition-colors"
                          >
                            {allSelected ? (
                              <CheckCircle className="w-5 h-5 text-primary-600" />
                            ) : someSelected ? (
                              <div className="w-5 h-5 rounded-full border-2 border-primary-600 bg-primary-100 relative">
                                <div className="absolute inset-1 bg-primary-600 rounded-full" />
                              </div>
                            ) : (
                              <Circle className="w-5 h-5 text-text-secondary" />
                            )}
                            <span className="font-medium">{formatResourceName(resource)}</span>
                          </button>
                          <Shield className="w-4 h-4 text-text-secondary" />
                        </div>
                        <span className="text-sm text-text-secondary">
                          {selectedInResource}/{permissions.length} selected
                        </span>
                      </div>
                    </div>

                    {/* Permissions */}
                    <div className="p-4 space-y-3">
                      {permissions.map((permission) => {
                        const isSelected = selectedPermissions.has(permission.id);
                        
                        return (
                          <div
                            key={permission.id}
                            className={`flex items-start space-x-3 p-3 rounded-lg border transition-all ${
                              isSelected
                                ? 'border-primary-200 bg-primary-50'
                                : 'border-border-primary bg-background-primary hover:border-primary-100'
                            }`}
                          >
                            <button
                              onClick={() => togglePermission(permission.id)}
                              className="flex-shrink-0 mt-0.5"
                            >
                              {isSelected ? (
                                <CheckCircle className="w-4 h-4 text-primary-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-text-secondary hover:text-primary-400 transition-colors" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-text-primary">
                                  {permission.displayName}
                                </span>
                                <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded">
                                  {permission.action}
                                </span>
                                {!permission.isActive && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <code className="text-xs text-text-secondary">{permission.name}</code>
                              {permission.description && (
                                <p className="text-sm text-text-secondary mt-1">
                                  {permission.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border-primary flex-shrink-0">
          <div className="text-sm text-text-secondary">
            {getChangesCount() > 0 ? (
              <span className="text-orange-600">
                {getChangesCount()} unsaved changes
              </span>
            ) : (
              'No changes'
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="secondary" 
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={isSaving || getChangesCount() === 0}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
